import { useEffect, useMemo, useRef, useState } from 'react'
import {
    getLowStockValidation,
    LowStockValidationResult
} from './getLowStockValidation'
import { getSalesUnitsConfig } from './getSalesUnitsConfig'

type LowStockProduct = {
    __typename?: string | null
    sku?: string | null
    stock_status?: string | null
    variants?: Array<{
        product?: {
            sku?: string | null
            stock_status?: string | null
        } | null
    } | null> | null
}

type LowStockBadgeState = {
    show: boolean
    label: string
}

type LowStockBadgesState = {
    lowStockBySku: Record<string, boolean>
    label: string
}

export const shouldShowLowStockBadge = (
    stock?: number | null,
    stockStatus?: string | null,
    available?: string | null,
    threshold?: number | null
): boolean => {
    return (
        typeof stock === 'number' &&
        typeof threshold === 'number' &&
        stock > 0 &&
        stock <= threshold &&
        stockStatus !== 'OUT_OF_STOCK' &&
        available !== '0'
    )
}

const getValidationBySku = (
    validations: LowStockValidationResult[]
): Map<string, LowStockValidationResult> => {
    return new Map(
        validations
            .filter((validation) => validation.sku)
            .map((validation) => [validation.sku as string, validation])
    )
}

const mergeLowStockUpdates = (
    current: LowStockBadgesState,
    updates: Record<string, boolean>,
    label: string
): LowStockBadgesState => {
    const lowStockBySku = { ...current.lowStockBySku }

    for (const [sku, showLowStockBadge] of Object.entries(updates)) {
        lowStockBySku[sku] = showLowStockBadge
    }

    return { lowStockBySku, label }
}

type LowStockValidationCandidate = {
    displaySku: string
    validationSkus: string[]
    stockStatus?: string | null
}

const getLowStockValidationCandidate = (
    product?: LowStockProduct | null,
    validateUnknownProductTypes = false
): LowStockValidationCandidate | null => {
    if (!product?.sku || product.stock_status === 'OUT_OF_STOCK') return null

    if (
        product.__typename === 'SimpleProduct' ||
        product.__typename === 'ProductSearchProduct'
    ) {
        return {
            displaySku: product.sku,
            validationSkus: [product.sku],
            stockStatus: product.stock_status
        }
    }

    if (product.__typename !== 'ConfigurableProduct') {
        if (!validateUnknownProductTypes) return null

        return {
            displaySku: product.sku,
            validationSkus: [product.sku],
            stockStatus: product.stock_status
        }
    }

    const variantSkus =
        product.variants
            ?.map((variant) => variant?.product?.sku)
            .filter((sku): sku is string => Boolean(sku)) ?? []

    return {
        displaySku: product.sku,
        validationSkus: Array.from(new Set(variantSkus)),
        stockStatus: product.stock_status
    }
}

export const useLowStockBadge = (
    product?: LowStockProduct | null,
    enabled = true
): LowStockBadgeState => {
    const [lowStockBadgeState, setLowStockBadgeState] =
        useState<LowStockBadgeState>({
            show: false,
            label: ''
        })
    const sku = product?.sku
    const stockStatus = product?.stock_status

    useEffect(() => {
        let cancelled = false

        const validateStock = async () => {
            if (!enabled || !sku || stockStatus === 'OUT_OF_STOCK') {
                setLowStockBadgeState({ show: false, label: '' })
                return
            }

            const config = await getSalesUnitsConfig()

            if (cancelled) return

            if (!config) {
                setLowStockBadgeState({ show: false, label: '' })
                return
            }

            try {
                const [validation] = await getLowStockValidation([{ sku }])
                const stock = Number(validation?.stock ?? 0)

                if (!cancelled) {
                    setLowStockBadgeState({
                        show: shouldShowLowStockBadge(
                            stock,
                            stockStatus,
                            validation?.available,
                            config.threshold
                        ),
                        label: config.label
                    })
                }
            } catch {
                if (!cancelled) {
                    setLowStockBadgeState({
                        show: false,
                        label: config.label
                    })
                }
            }
        }

        validateStock()

        return () => {
            cancelled = true
        }
    }, [enabled, sku, stockStatus])

    return lowStockBadgeState
}

export const useLowStockBadges = (
    products: Array<LowStockProduct | null | undefined>,
    enabled = true,
    validateUnknownProductTypes = false
): LowStockBadgesState => {
    const [lowStockBadgeState, setLowStockBadgeState] =
        useState<LowStockBadgesState>({
            lowStockBySku: {},
            label: ''
        })
    const requestedSkusRef = useRef<Set<string>>(new Set())
    const inFlightSkusRef = useRef<Set<string>>(new Set())
    const isMountedRef = useRef(true)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const candidatesToValidate = useMemo(() => {
        if (!enabled) return []

        const bySku = new Map<string, LowStockValidationCandidate>()

        for (const product of products) {
            const candidate = getLowStockValidationCandidate(
                product,
                validateUnknownProductTypes
            )
            if (!candidate) continue

            bySku.set(candidate.displaySku, candidate)
        }

        return Array.from(bySku.values())
    }, [enabled, products, validateUnknownProductTypes])

    const productsToValidateKey = candidatesToValidate
        .map(
            (candidate) =>
                `${candidate.displaySku}:${candidate.validationSkus.join(
                    ','
                )}:${candidate.stockStatus ?? ''}`
        )
        .join('|')

    useEffect(() => {
        const validateStock = async () => {
            if (!enabled || candidatesToValidate.length === 0) {
                setLowStockBadgeState((current) => ({
                    lowStockBySku: current.lowStockBySku,
                    label: ''
                }))
                return
            }

            const config = await getSalesUnitsConfig()

            if (!config) {
                setLowStockBadgeState({
                    lowStockBySku: {},
                    label: ''
                })
                return
            }

            const missingCandidates = candidatesToValidate.filter(
                (candidate) =>
                    !requestedSkusRef.current.has(candidate.displaySku) &&
                    !inFlightSkusRef.current.has(candidate.displaySku)
            )

            if (missingCandidates.length === 0) {
                setLowStockBadgeState((current) => {
                    if (current.label === config.label) return current

                    return {
                        lowStockBySku: current.lowStockBySku,
                        label: config.label
                    }
                })
                return
            }

            for (const candidate of missingCandidates) {
                inFlightSkusRef.current.add(candidate.displaySku)
            }

            try {
                const validationSkus = Array.from(
                    new Set(
                        missingCandidates.flatMap(
                            (candidate) => candidate.validationSkus
                        )
                    )
                )
                const validations = await getLowStockValidation(
                    validationSkus.map((sku) => ({ sku }))
                )

                const validationBySku = getValidationBySku(validations)

                const lowStockUpdates: Record<string, boolean> = {}

                for (const candidate of missingCandidates) {
                    inFlightSkusRef.current.delete(candidate.displaySku)
                    requestedSkusRef.current.add(candidate.displaySku)

                    const availableValidations = candidate.validationSkus
                        .map((sku) => validationBySku.get(sku))
                        .filter(
                            (
                                validation
                            ): validation is LowStockValidationResult => {
                                const stock = Number(validation?.stock ?? 0)
                                return (
                                    Boolean(validation?.sku) &&
                                    validation?.available !== '0' &&
                                    stock > 0
                                )
                            }
                        )

                    if (availableValidations.length !== 1) {
                        lowStockUpdates[candidate.displaySku] = false
                        continue
                    }

                    const validation = availableValidations[0]
                    const stock = Number(validation.stock ?? 0)

                    lowStockUpdates[candidate.displaySku] =
                        shouldShowLowStockBadge(
                            stock,
                            candidate.stockStatus,
                            validation.available,
                            config.threshold
                        )
                }

                if (!isMountedRef.current) return

                setLowStockBadgeState((current) =>
                    mergeLowStockUpdates(current, lowStockUpdates, config.label)
                )
            } catch {
                const lowStockUpdates: Record<string, boolean> = {}

                for (const candidate of missingCandidates) {
                    inFlightSkusRef.current.delete(candidate.displaySku)
                    requestedSkusRef.current.add(candidate.displaySku)
                    lowStockUpdates[candidate.displaySku] = false
                }

                if (!isMountedRef.current) return

                setLowStockBadgeState((current) =>
                    mergeLowStockUpdates(current, lowStockUpdates, config.label)
                )
            }
        }

        validateStock()
        // Re-run when the validated product list changes; refs above prevent
        // duplicate stock calls for already requested SKUs.
    }, [enabled, productsToValidateKey])

    return lowStockBadgeState
}
